import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { PatientList, PatientDetail, PatientPaginationResponse } from "../interfaces/Patients";
import apiClient from "../api/apiClient";

interface PatientState {
  loading: boolean;
  actionLoading: boolean;
  user: any | null;
  error: string | null;
  isLoggedIn: boolean;
  data: PatientList[] | null;
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  selectedPatient: PatientDetail | null;
}

const initialState: PatientState = {
  loading: false,
  actionLoading: false,
  user: null,
  error: null,
  isLoggedIn: false,
  data: null,
  total: 0,
  page: 1,
  limit: 10,
  totalPages: 1,
  selectedPatient: null,
};

export const AllPatientList = createAsyncThunk(
  "patient/AllPatientList",
  async (params: { page?: number; limit?: number; search?: string } | undefined, thunkAPI) => {
    try {
      const page = params?.page || 1;
      const limit = params?.limit || 10;
      const search = params?.search ? params.search.trim() : "";
      const queryParams = new URLSearchParams({
        page: String(page),
        limit: String(limit),
        ...(search ? { search } : {}),
      });

      const response = await apiClient.get(`/api/v1/patients?${queryParams.toString()}`);
      return response.data.data;
    } catch (error: any) {
      const message = error.response?.data?.message || error.message || "Failed to fetch patients";
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const GetPatientDetails = createAsyncThunk(
  "patient/GetPatientDetails",
  async (id: string, thunkAPI) => {
    try {
      const response = await apiClient.get(`/api/v1/patients/${id}`);
      return response.data.data;
    } catch (error: any) {
      const message = error.response?.data?.message || error.message || "Failed to fetch patient details";
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const UpdatePatientInfo = createAsyncThunk(
  "patient/UpdatePatientInfo",
  async ({ id, fullName, email, phoneNo }: { id: string; fullName: string; email: string; phoneNo?: string }, thunkAPI) => {
    try {
      const response = await apiClient.put(`/api/v1/patients/${id}`, { fullName, email, phoneNo });
      return response.data.data;
    } catch (error: any) {
      const message = error.response?.data?.error?.message || error.response?.data?.message || error.message || "Failed to update patient";
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const AdminSetPassword = createAsyncThunk(
  "patient/AdminSetPassword",
  async ({ id, password }: { id: string; password: string }, thunkAPI) => {
    try {
      const response = await apiClient.post(`/api/v1/patients/${id}/password`, { password });
      return response.data.data;
    } catch (error: any) {
      const message = error.response?.data?.error?.message || error.response?.data?.message || error.message || "Failed to set password";
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const AdminSendResetEmail = createAsyncThunk(
  "patient/AdminSendResetEmail",
  async (id: string, thunkAPI) => {
    try {
      const response = await apiClient.post(`/api/v1/patients/${id}/send-reset-email`);
      return response.data;
    } catch (error: any) {
      const message = error.response?.data?.error?.message || error.response?.data?.message || error.message || "Failed to send reset email";
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const AdminEnrollPatient = createAsyncThunk(
  "patient/AdminEnrollPatient",
  async ({ patientId, programId }: { patientId: string; programId: string }, thunkAPI) => {
    try {
      const response = await apiClient.post(`/api/v1/patients/${patientId}/enroll`, { programId });
      return response.data.data;
    } catch (error: any) {
      const message = error.response?.data?.error?.message || error.response?.data?.message || error.message || "Failed to enroll patient";
      return thunkAPI.rejectWithValue(message);
    }
  }
);

const patientSlice = createSlice({
  name: "patient",
  initialState,
  reducers: {
    resetState: () => initialState,
    clearSelectedPatient: (state) => {
      state.selectedPatient = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // AllPatientList
      .addCase(AllPatientList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(AllPatientList.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload;
        if (payload?.patients) {
          state.data = payload.patients;
          state.total = payload.total || 0;
          state.page = payload.page || 1;
          state.limit = payload.limit || 10;
          state.totalPages = payload.totalPages || 1;
        } else if (Array.isArray(payload)) {
          state.data = payload;
          state.total = payload.length;
        } else if (payload?.user) {
          state.data = payload.user;
          state.total = payload.total || payload.user.length;
        }
      })
      .addCase(AllPatientList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // GetPatientDetails
      .addCase(GetPatientDetails.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(GetPatientDetails.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.selectedPatient = action.payload;
      })
      .addCase(GetPatientDetails.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload as string;
      })

      // UpdatePatientInfo
      .addCase(UpdatePatientInfo.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(UpdatePatientInfo.fulfilled, (state, action) => {
        state.actionLoading = false;
        const updated = action.payload;
        if (state.data && updated) {
          state.data = state.data.map((p) => (p.id === updated.id ? { ...p, ...updated } : p));
        }
      })
      .addCase(UpdatePatientInfo.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload as string;
      })

      // AdminSetPassword
      .addCase(AdminSetPassword.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(AdminSetPassword.fulfilled, (state) => {
        state.actionLoading = false;
      })
      .addCase(AdminSetPassword.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload as string;
      })

      // AdminSendResetEmail
      .addCase(AdminSendResetEmail.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(AdminSendResetEmail.fulfilled, (state) => {
        state.actionLoading = false;
      })
      .addCase(AdminSendResetEmail.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload as string;
      })

      // AdminEnrollPatient
      .addCase(AdminEnrollPatient.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(AdminEnrollPatient.fulfilled, (state) => {
        state.actionLoading = false;
      })
      .addCase(AdminEnrollPatient.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { resetState, clearSelectedPatient } = patientSlice.actions;
export default patientSlice.reducer;

